/** Блок «Доработать» на карточке результата: новая версия кадра по текстовому замечанию. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { btnPrimary, ErrorLine, input } from "@/components/trends/ui";
import { fittingKeys, loadFittingOptions } from "@/lib/fitting";
import { usd } from "@/lib/project";
import { editRunView } from "@/lib/editRuns";
import { thumbUrl } from "@/lib/media";
import { editEstimate, editVariant, resultPath, type ResultCard } from "@/lib/results";

const NOTE_MAX = 1000;
const BASIS_LABEL = { facts: "по фактам", formula: "по формуле", table: "по таблице пробы" } as const;

type Runs = NonNullable<ResultCard["edits"]>;

export function EditPanel({
  variantId,
  model,
  runs,
  onStarted,
}: {
  variantId: number;
  model: string;
  runs: Runs;
  onStarted: () => void;
}) {
  /** id запущенной доработки: пока её нет в `runs`, показываем «Запущено» */
  const [launched, setLaunched] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [chosen, setChosen] = useState("");
  const [n, setN] = useState(1);
  const [key, setKey] = useState(() => crypto.randomUUID());

  const options = useQuery({ queryKey: fittingKeys.options, queryFn: loadFittingOptions, staleTime: 60_000 });
  const models = (options.data?.models ?? []).filter((m) => !m.disabled);
  const current = chosen || model;
  const selected = models.some((m) => m.code === current) ? current : (models[0]?.code ?? current);
  const maxN = Math.min(4, models.find((m) => m.code === selected)?.max_n ?? 4);
  const count = Math.min(n, maxN);

  const est = useQuery({
    queryKey: ["edit-estimate", variantId, selected, count],
    queryFn: () => editEstimate(variantId, selected, count),
    enabled: !!options.data,
    retry: false,
  });

  const touch = () => {
    setKey(crypto.randomUUID());
    start.reset();
  };

  const start = useMutation({
    mutationFn: () => editVariant(variantId, { note: note.trim(), model: selected, n: count, idempotency_key: key }),
    onSuccess: (r) => {
      setLaunched(r.generation_id);
      setNote("");
      setKey(crypto.randomUUID());
      onStarted();
    },
  });

  const price = est.data ? `~${usd(est.data.cost)}` : "";
  const canRun = note.trim().length > 0 && !start.isPending && !!est.data && !est.isError;

  return (
    <form
      className="grid gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (canRun) start.mutate();
      }}
    >
      <label className="grid gap-1 text-[12px] text-fg-muted">
        Что изменить
        <textarea
          className={input}
          rows={3}
          maxLength={NOTE_MAX}
          required
          placeholder="например: вид спереди, а не сзади"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            touch();
          }}
        />
      </label>
      <div className="flex flex-wrap items-center gap-2 text-[12px] text-fg-muted">
        <select aria-label="Модель" className={input} value={selected} onChange={(e) => {
            setChosen(e.target.value);
            touch();
          }}>
          {(models.length ? models : [{ code: selected, label: selected }]).map((m) => (
            <option key={m.code} value={m.code}>
              {m.label}
            </option>
          ))}
        </select>
        <select aria-label="Число кадров" className={input} value={count} onChange={(e) => {
            setN(Number(e.target.value));
            touch();
          }}>
          {Array.from({ length: maxN }, (_, i) => i + 1).map((v) => (
            <option key={v} value={v}>
              {v} кадр.
            </option>
          ))}
        </select>
        {est.data ? <span>{BASIS_LABEL[est.data.basis]}</span> : null}
      </div>
      <div>
        <button type="submit" className={btnPrimary} disabled={!canRun}>
          {start.isPending ? "Ставлю…" : `Доработать${price ? ` · ${price}` : ""}`}
        </button>
      </div>
      <ErrorLine error={options.error ?? est.error ?? start.error} />
      {launched !== null && !runs.some((r) => r.generation_id === launched) ? (
        <div className="text-[12px] text-fg-muted">Запущено — результат появится здесь</div>
      ) : null}
      {runs.length ? (
        <ul className="grid gap-2">
          {[...runs]
            .sort((a, b) => b.generation_id - a.generation_id)
            .map((r) => {
              const v = editRunView(r);
              return (
                <li key={r.generation_id} className="grid gap-1 text-[12px]">
                  {v.state === "done" ? (
                    <div className="flex flex-wrap gap-1">
                      {v.items.map((it) => (
                        <Link key={it.variantId} to={resultPath(it.variantId)} title={`вариант #${it.variantId}`}>
                          <img
                            src={thumbUrl(it.path)}
                            alt={`вариант #${it.variantId}`}
                            loading="lazy"
                            className="size-16 rounded-[4px] border border-border object-cover"
                          />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <span className={v.state === "failed" ? "text-err" : "text-fg-muted"}>{v.label}</span>
                  )}
                  {r.note ? <span className="text-[11px] text-fg-muted">{r.note}</span> : null}
                </li>
              );
            })}
        </ul>
      ) : null}
    </form>
  );
}
